import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader } from "Common/Loader";
import { getAnnouncementById, getPublicAnnouncementById } from "../../helpers/backend_helper";

interface AnnouncementDetail {
  announcementId: number;
  title: string;
  type: string;
  startDate: string;
  endDate: string;
  bodyEnglish: string;
  bodySpanish: string;
  shortDescription: string;
}

const AnnouncementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState<AnnouncementDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getTypeClass = (type: string) => {
    switch (type?.toLowerCase()) {
      case 'important':
        return 'bg-blue-100 text-blue-800';
      case 'event':
        return 'bg-green-100 text-green-800';
      case 'emergency':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    const loadAnnouncement = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await getPublicAnnouncementById(Number(id));
        
        if (response?.data) {
          setAnnouncement(response.data[0]);
        } else {
          setError('Announcement not found');
        }
      } catch (error) {
        console.error('Error loading announcement:', error);
        setError('Failed to load announcement details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      loadAnnouncement();
    }
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="mb-4 p-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-red-900/50 dark:text-red-200" role="alert">
            {error}
            <button 
              type="button" 
              className="ml-2 text-red-800 hover:text-red-900 dark:text-red-200 dark:hover:text-red-100"
              onClick={() => navigate(-1)}
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!announcement) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <span className={`${getTypeClass(announcement.type)} text-xs font-semibold px-2.5 py-0.5 rounded`}>
            {announcement.type || 'Event'}
          </span>
          <span className="text-gray-500 text-sm">
            {new Date(announcement.startDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </span>
        </div>
        <button
          onClick={() => navigate(-1)}
          className="text-blue-600 font-medium hover:text-blue-800 transition"
        >
          ← Back to Home
        </button>
      </div>

      <h1 className="text-3xl font-bold text-gray-800 mb-8">{announcement.title}</h1>
      
      <div className="space-y-6">
        {/* English Version */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <span className="text-lg font-semibold text-gray-800">English Version</span>
              {/* <div className="ml-2 h-4 w-6">
                <img src={require("../../assets/images/flags/us.svg")} alt="English" className="h-full w-full" />
              </div> */}
            </div>
            <div 
              className="prose max-w-none text-gray-600"
              dangerouslySetInnerHTML={{ __html: announcement.bodyEnglish }}
            />
          </div>
        </div>

        {/* Spanish Version */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <span className="text-lg font-semibold text-gray-800">Versión en Español</span>
              {/* <div className="ml-2 h-4 w-6">
                <img src={require("../../assets/images/flags/es.svg")} alt="Spanish" className="h-full w-full" />
              </div> */}
            </div>
            <div 
              className="prose max-w-none text-gray-600"
              dangerouslySetInnerHTML={{ __html: announcement.bodySpanish }}
            />
          </div>
        </div>
      </div>

      {announcement.endDate && (
        <div className="mt-8 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center text-sm text-gray-500">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span>Valid until: {new Date(announcement.endDate).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnouncementDetail; 